import React from "react";

/**
 * Global error boundary — captura crashes em qualquer subcomponente e
 * mostra um fallback amigável em vez de tela branca.
 *
 * Uso: <ErrorBoundary><Routes>...</Routes></ErrorBoundary>
 *
 * Pode-se passar `fallback` (componente) ou `fallbackRender({error, reset})`.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to console for debugging in preview / production
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallbackRender) {
        return this.props.fallbackRender({ error: this.state.error, reset: this.reset });
      }
      return (
        <div className="min-h-[60vh] grid place-items-center p-6" data-testid="error-boundary-fallback">
          <div className="max-w-md w-full bg-white dark:bg-[#121417] border border-rose-200/70 dark:border-rose-900/40 rounded-xl p-6 space-y-3 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-rose-50 dark:bg-rose-950/40 grid place-items-center">
              <span className="text-rose-600 text-xl">⚠</span>
            </div>
            <h2 className="text-lg font-semibold tracking-tight">Algo deu errado nesta tela</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Encontramos um erro ao renderizar este conteúdo. Os outros menus continuam funcionando normalmente.
            </p>
            {this.state.error?.message && (
              <pre className="text-[10px] bg-neutral-50 dark:bg-white/[0.04] rounded p-2 text-left overflow-auto max-h-40 text-neutral-600 dark:text-neutral-400">
                {String(this.state.error.message).slice(0, 500)}
              </pre>
            )}
            <div className="flex gap-2 justify-center">
              <button
                onClick={this.reset}
                className="px-4 py-2 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800"
                data-testid="error-boundary-retry"
              >
                Tentar novamente
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-full border border-neutral-300 dark:border-white/[0.1] text-sm font-medium hover:bg-neutral-50 dark:hover:bg-white/[0.04]"
                data-testid="error-boundary-reload"
              >
                Recarregar página
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
